import { useState, useCallback, useMemo } from "react";

import { subMonths, format } from "date-fns";

import { useObjectMemo } from "../../../hooks";
import { useSubscriptionsForCard } from "../../entities/subscriptions";
import {
  LAST_12_MONTHS,
  MONTH_LABELS_SMALL,
  MONTH_LABELS_LARGE,
  MONTH_FULL_NAME,
} from "../../entities/subscriptions/constants";

import type { DataPoint } from "../../entities/subscriptions/types";

export const useCurrentMonth = (): DateString => {
  return useMemo(() => format(new Date(), "yyyy-MM-dd") as DateString, []);
};

export const use13MonthsAgo = (): DateString => {
  return useMemo(
    () => format(subMonths(new Date(), 13), "yyyy-MM-dd") as DateString,
    [],
  );
};

export const useMerchantPaymentHistory = (
  cardId: API.CardId,
  merchantName: string,
) => {
  const subscriptionsForMonths = useSubscriptionsForCard(cardId);
  const paymentHistory = useMemo(() => {
    if (!subscriptionsForMonths) return [];

    return subscriptionsForMonths.data
      .flatMap((monthBreakdown) =>
        monthBreakdown.transactions.filter(
          (transaction) => transaction.merchant_name === merchantName,
        ),
      )
      .reverse();
  }, [merchantName, subscriptionsForMonths]);

  const inferredYearlySavings = paymentHistory.reduce((acc, payment) => {
    const givenDate = new Date(payment.transaction_date);
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return givenDate > oneYearAgo ? acc + payment.amount : acc;
  }, 0);

  return useObjectMemo({
    paymentHistory,
    inferredYearlySavings,
  });
};

const DEFAULT_DATA = {
  data: [] as API.SubscriptionsPerMonth[],
} as const;
export function useSubscriptionsChartData(cardId: API.CardId, size: string) {
  const [selectedMonth, setSelectedMonth] = useState<MonthString>(
    LAST_12_MONTHS[LAST_12_MONTHS.length - 1],
  );
  const monthLabelLookup = ["xs", "s"].includes(size)
    ? MONTH_LABELS_SMALL
    : MONTH_LABELS_LARGE;

  const { data: transactionData } =
    useSubscriptionsForCard(cardId) || DEFAULT_DATA;

  const data: DataPoint[] = useMemo(
    () =>
      transactionData.map(({ month, total }) => ({
        month,
        total,
      })) ?? [],
    [transactionData],
  );

  const selectedMonthIndex = data.findIndex(
    (datum) => datum.month === selectedMonth,
  );
  const prevMonth =
    selectedMonthIndex > 0 ? data[selectedMonthIndex - 1].month : null;
  const nextMonth =
    selectedMonthIndex < data.length - 1
      ? data[selectedMonthIndex + 1].month
      : null;
  const year = selectedMonth.slice(0, 4);
  const prev12MonthsTotal = useMemo(
    () => data.slice(1, 12).reduce((sum, p) => sum + p.total, 0),
    [data],
  );
  const monthTotal = data[selectedMonthIndex]?.total ?? 0;

  const onMonthChange = useCallback(
    (m: MonthString) => setSelectedMonth(m),
    [],
  );

  const monthLabels = useMemo<Record<string, string>>(() => {
    // TODO: Update this to use the API data when available
    // https://linear.app/narmi/issue/CDB-1044/integrate-the-api-subscriptions-and-merchant-info-removing-the-mock
    return LAST_12_MONTHS.reduce(
      (acc, date) => {
        const month = date.slice(5) as keyof typeof monthLabelLookup;
        acc[month] = monthLabelLookup[month];
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [monthLabelLookup]);

  const selectedMonthFullName = useMemo(() => {
    return MONTH_FULL_NAME[
      selectedMonth.slice(5) as keyof typeof monthLabelLookup
    ];
  }, [selectedMonth]);

  const isSelectedThisMonth = useMemo(
    () => selectedMonthIndex === data.length - 1,
    [selectedMonthIndex, data.length],
  );
  const selectedTransactions = useMemo(
    () =>
      transactionData.find(
        (transaction) => transaction.month === selectedMonth,
        [selectedMonth],
      )?.transactions,
    [selectedMonth, transactionData],
  );

  return useObjectMemo({
    data,
    monthLabels,
    selectedMonth,
    prevMonth,
    nextMonth,
    year,
    prev12MonthsTotal,
    monthTotal,
    selectedMonthFullName,
    isSelectedThisMonth,
    onMonthChange,
    selectedTransactions,
    allTransactions: transactionData,
  });
}
